import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import MosaicCube from "@/components/MosaicCube";
import BrandMarquee from "@/components/BrandMarquee";
import { MaskedLines, Fade, Overline } from "@/components/Reveal";
import { EXPERTISE, WHY_MOSAICON, METHODOLOGY, AUDIENCES, IMAGES } from "@/data/content";

const BENTO_SPANS = [
  "lg:col-span-5", "lg:col-span-7", "lg:col-span-4", "lg:col-span-8",
  "lg:col-span-7", "lg:col-span-5", "lg:col-span-6", "lg:col-span-6",
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const cubeY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const cubeRotate = useTransform(scrollYProgress, [0, 1], [0, 14]);

  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="hero-section">
      {/* floating tiles */}
      <motion.div aria-hidden="true" className="absolute top-24 left-[6%] h-3 w-3 bg-gold/70 hidden md:block" style={{ y: cubeY }} />
      <motion.div aria-hidden="true" className="absolute bottom-32 left-[42%] h-2 w-2 bg-rust/60 hidden md:block" style={{ y: cubeY }} />
      <motion.div aria-hidden="true" className="absolute top-40 right-[38%] h-2.5 w-2.5 border border-teal/50 hidden lg:block" style={{ y: cubeY }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 md:pt-28 md:pb-32 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="text-xs uppercase tracking-[0.3em] font-medium text-burgundy mb-8"
            data-testid="hero-overline"
          >
            Boutique Consultancy — Greater Toronto Area
          </motion.p>
          <h1 className="font-serif text-5xl md:text-7xl xl:text-[5.25rem] leading-[1.04] tracking-tighter text-ink" data-testid="hero-headline">
            <MaskedLines
              delay={0.25}
              lines={[
                "Strategy that",
                "connects people,",
                <>ideas and <em className="text-burgundy">opportunity</em>.</>,
              ]}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-ink/70"
            data-testid="hero-subtext"
          >
            Mosaicon Strategies is a Greater Toronto Area boutique consultancy specializing in strategic
            communications, media relations, public affairs, stakeholder and community engagement,
            multicultural outreach and political consulting.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/what-we-do"
              data-testid="hero-explore-btn"
              className="group bg-burgundy text-cream text-xs uppercase tracking-[0.2em] font-semibold px-8 py-4 inline-flex items-center gap-3 transition-colors duration-300 hover:bg-ink"
            >
              Explore Our Expertise
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-conversation-btn"
              className="border border-ink/30 text-ink text-xs uppercase tracking-[0.2em] font-semibold px-8 py-4 transition-colors duration-300 hover:bg-ink hover:text-cream hover:border-ink"
            >
              Start a Conversation
            </Link>
          </motion.div>
        </div>
        <div className="lg:col-span-5 hidden lg:flex justify-center">
          <motion.div style={{ y: cubeY, rotate: cubeRotate }} className="w-72 xl:w-96">
            <MosaicCube mode="load" delay={0.5} className="w-full h-auto drop-shadow-sm" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Intro = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-30, 30]);

  return (
    <section ref={ref} className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32" data-testid="intro-section">
      <div className="grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7">
          <Overline>01 — Who We Are</Overline>
          <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-[1.1] tracking-tight text-ink" data-testid="intro-heading">
            <MaskedLines immediate={false} lines={["Communications with purpose.", "Strategy with perspective."]} />
          </h2>
          <Fade delay={0.15} className="mt-8 space-y-6 text-base leading-relaxed text-ink/70 max-w-2xl">
            <p>
              In a rapidly changing communications and public affairs environment, organizations need more than
              visibility. They need clarity, credibility, meaningful relationships and a strategy that connects
              communications to their larger objectives.
            </p>
            <p>
              Mosaicon Strategies works with clients to understand the environment around them, identify
              opportunities and challenges, develop compelling messages, engage the people who matter and
              translate strategy into action.
            </p>
            <p>
              From the media and the boardroom to community organizations and government, we help clients
              communicate with confidence and navigate complexity.
            </p>
            <p>
              Our boutique model allows clients to work directly with experienced senior consultants while
              drawing upon a trusted network of specialized professionals when a project requires additional
              expertise.
            </p>
          </Fade>
          <Fade delay={0.25} className="mt-10">
            <Link
              to="/who-we-are"
              data-testid="discover-mosaicon-btn"
              className="group inline-flex items-center gap-3 text-sm uppercase tracking-[0.18em] font-semibold text-burgundy"
            >
              <span className="link-underline">Discover Mosaicon</span>
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Fade>
        </div>
        <div className="lg:col-span-5">
          <Fade delay={0.2} className="relative">
            <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-gold" aria-hidden="true" />
            <div className="overflow-hidden">
              <motion.img
                src={IMAGES.boardroom}
                alt="A quiet, light-filled boardroom table — the setting for senior-level strategic counsel"
                className="w-full aspect-[4/5] object-cover scale-110"
                style={{ y: imgY }}
                loading="lazy"
                data-testid="intro-image"
              />
            </div>
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-ink/40">
              Senior-level counsel, directly with the people who decide
            </p>
          </Fade>
        </div>
      </div>
    </section>
  );
};

const Expertise = () => (
  <section className="bg-white border-y border-ink/10" data-testid="expertise-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
        <div>
          <Overline>02 — Core Expertise</Overline>
          <h2 className="mt-6 font-serif text-4xl md:text-5xl tracking-tight text-ink" data-testid="expertise-heading">
            <MaskedLines immediate={false} lines={["One strategy.", "Many disciplines."]} />
          </h2>
        </div>
        <Fade delay={0.1}>
          <p className="max-w-sm text-sm leading-relaxed text-ink/60">
            Reputation, relationships and communications are closely connected. Our strength is bringing these
            disciplines together rather than treating each separately.
          </p>
        </Fade>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-px bg-ink/10 border border-ink/10">
        {EXPERTISE.map((item, i) => (
          <Fade key={item.title} delay={i * 0.05} y={20} className={`md:col-span-1 ${BENTO_SPANS[i]}`}>
            <Link
              to="/what-we-do"
              data-testid={`expertise-card-${i + 1}`}
              className={`group block h-full bg-white p-8 md:p-10 transition-all duration-500 hover:-translate-y-1 ${
                i % 2 === 0 ? "hover:shadow-[inset_0_0_0_1px_#D2A74B]" : "hover:shadow-[inset_0_0_0_1px_#00474B]"
              }`}
            >
              <p className="font-serif text-sm text-burgundy/60">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="mt-4 font-serif text-2xl leading-snug tracking-tight text-ink group-hover:text-burgundy transition-colors duration-300">
                {item.title}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-ink/60">{item.copy}</p>
              <ArrowRight className="mt-6 h-4 w-4 text-ink/30 transition-all duration-300 group-hover:translate-x-1 group-hover:text-burgundy" />
            </Link>
          </Fade>
        ))}
      </div>
      <Fade delay={0.1} className="mt-10">
        <Link
          to="/what-we-do"
          data-testid="view-all-services-btn"
          className="group inline-flex items-center gap-3 text-sm uppercase tracking-[0.18em] font-semibold text-ink hover:text-burgundy transition-colors duration-300"
        >
          <span className="link-underline">View All Services</span>
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Fade>
    </div>
  </section>
);

const Why = () => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32" data-testid="why-section">
    <Overline>03 — Why Mosaicon</Overline>
    <h2 className="mt-6 font-serif text-4xl md:text-5xl tracking-tight text-ink max-w-2xl" data-testid="why-heading">
      <MaskedLines immediate={false} lines={["Where strategy meets", "relationships."]} />
    </h2>
    <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 border-t border-ink/15">
      {WHY_MOSAICON.map((item, i) => (
        <Fade key={item.title} delay={i * 0.08} className="border-b lg:border-b-0 lg:border-r last:border-r-0 border-ink/15 px-2 lg:px-8 py-10 first:lg:pl-0">
          <p className="font-serif text-4xl text-gold">{String(i + 1).padStart(2, "0")}</p>
          <h3 className="mt-5 font-serif text-xl tracking-tight text-ink">{item.title}</h3>
          <p className="mt-4 text-sm leading-relaxed text-ink/60">{item.copy}</p>
        </Fade>
      ))}
    </div>
  </section>
);

const MethodBlock = ({ index }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const colors = ["#610C27", "#00474B", "#D2A74B"];
  return (
    <svg ref={ref} viewBox="0 0 36 36" className="h-9 w-9" aria-hidden="true">
      {colors.map((c, i) => (
        <motion.rect
          key={c}
          x={4 + i * 9}
          y={4 + i * 9}
          width="14"
          height="14"
          fill={i === 2 ? "none" : c}
          stroke={i === 2 ? c : "none"}
          strokeWidth="1.5"
          initial={{ x: -14 + i * 12, y: 10 - i * 8, opacity: 0 }}
          animate={
            inView
              ? { x: 0, y: 0, opacity: 1 }
              : { x: -14 + i * 12, y: 10 - i * 8, opacity: 0 }
          }
          transition={{ duration: 0.9, delay: 0.1 + index * 0.1 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
        />
      ))}
    </svg>
  );
};

const Methodology = () => (
  <section className="bg-sand/60 border-y border-ink/10" data-testid="methodology-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
      <Overline>04 — Our Approach</Overline>
      <h2 className="mt-6 font-serif text-3xl md:text-5xl tracking-tight text-ink" data-testid="methodology-heading">
        <MaskedLines immediate={false} lines={["Listen. Understand. Strategize.", "Connect. Deliver."]} />
      </h2>
      <div className="mt-16 grid md:grid-cols-3 lg:grid-cols-5 gap-10">
        {METHODOLOGY.map((step, i) => (
          <Fade key={step.title} delay={i * 0.08} data-testid={`methodology-step-${i + 1}`}>
            <MethodBlock index={i} />
            <p className="mt-6 text-xs uppercase tracking-[0.25em] text-burgundy font-semibold">
              {String(i + 1).padStart(2, "0")} — {step.title}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-ink/60">{step.copy}</p>
          </Fade>
        ))}
      </div>
    </div>
  </section>
);

const Audiences = () => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32" data-testid="audiences-section">
    <div className="grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <Overline>05 — Who We Work With</Overline>
        <h2 className="mt-6 font-serif text-3xl md:text-4xl tracking-tight text-ink">
          Mosaicon&apos;s services are suited to:
        </h2>
      </div>
      <div className="lg:col-span-8">
        <ul className="grid sm:grid-cols-2 gap-x-12">
          {AUDIENCES.map((a, i) => (
            <Fade key={a} delay={i * 0.04} y={12}>
              <li className="flex items-baseline gap-4 py-4 border-b border-ink/10 text-sm md:text-base text-ink/80">
                <span className="font-serif text-burgundy/60 text-sm">{String(i + 1).padStart(2, "0")}</span>
                {a}
              </li>
            </Fade>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const ClosingCTA = () => (
  <section className="bg-burgundy text-cream relative overflow-hidden" data-testid="closing-cta">
    <div className="absolute -right-16 -bottom-24 w-96 opacity-20 pointer-events-none" aria-hidden="true">
      <MosaicCube className="w-full h-auto" />
    </div>
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-28 md:py-36 relative">
      <p className="text-xs uppercase tracking-[0.3em] text-gold font-medium" data-testid="cta-overline">
        Begin the conversation
      </p>
      <h2 className="mt-6 font-serif text-4xl md:text-6xl tracking-tighter leading-[1.05]" data-testid="cta-heading">
        <MaskedLines immediate={false} lines={["Every challenge has", "different pieces."]} />
      </h2>
      <Fade delay={0.1}>
        <p className="mt-6 font-serif italic text-2xl md:text-3xl text-gold">We help you see how they connect.</p>
      </Fade>
      <Fade delay={0.2}>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-cream/80">
          Whether you are building your reputation, communicating through change, entering a new community,
          engaging stakeholders, navigating government or preparing for a campaign, it starts with the right
          strategy.
        </p>
      </Fade>
      <Fade delay={0.3} className="mt-10">
        <Link
          to="/contact"
          data-testid="cta-start-conversation-btn"
          className="inline-block bg-cream text-burgundy text-xs uppercase tracking-[0.22em] font-bold px-10 py-5 transition-colors duration-300 hover:bg-gold hover:text-ink"
        >
          Let&apos;s Start a Conversation
        </Link>
      </Fade>
      <p className="mt-16 text-xs uppercase tracking-[0.28em] text-cream/50" data-testid="cta-brand-line">
        Connecting communities. Opening possibilities. Navigating opportunities.
      </p>
    </div>
  </section>
);

const Home = () => (
  <>
    <SEO
      title="Mosaicon Strategies | Strategic Communications, Public Affairs & Political Consulting — Toronto"
      description="Mosaicon Strategies is a Greater Toronto Area boutique consultancy specializing in strategic communications, media relations, public affairs, stakeholder and community engagement, multicultural outreach and political consulting."
    />
    <Hero />
    <BrandMarquee />
    <Intro />
    <Expertise />
    <Why />
    <Methodology />
    <Audiences />
    <ClosingCTA />
  </>
);

export default Home;
